import {t} from 'i18next';
import {GridFilterModel, GridSortModel} from '@mui/x-data-grid';
import {ProtocolIntelligenceListRequest} from '../../../../api/types/aiReview.type';
import {formatUTCDateTime} from '../../../../utils/utils';

export const protocolListTableProps = {
  ROW_HEIGHT: 36,
  FILTER_HEADER_HEIGHT: 48,
  DEFAULT_PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
};

export interface IProtocolColumnsData {
  field: string;
  headerName: string;
  fieldDataKey: string;
  filterable: boolean;
  sortable: boolean;
  width: number;
  formatFunction?: (value: unknown, row?: Record<string, unknown>) => string;
}

export const formatProtocolDate = (dateString: string | null | undefined) =>
  dateString?.trim() ? formatUTCDateTime(dateString) : '---';

/** Grid field → API sortingColumn / columnFilter.columnName */
export const PROTOCOL_LIST_FIELD_TO_API_COLUMN: Record<string, string> = {
  protocolName: 'ProtocolName',
  createdBy: 'CreatedBy',
  status: 'Status',
  lastUpdatedAt: 'LastUpdatedAt',
  latestVersion: 'LatestVersion',
  source: 'Source',
  isActive: 'IsActive',
  protocolId: 'ReviewProtocolID',
};

const PROTOCOL_LIST_API_COLUMNS = new Set(
  Object.values(PROTOCOL_LIST_FIELD_TO_API_COLUMN)
);

const DEFAULT_PROTOCOL_SORT_COLUMN =
  PROTOCOL_LIST_FIELD_TO_API_COLUMN.lastUpdatedAt;

export const resolveProtocolSortingColumn = (field?: string): string => {
  if (!field) {
    return DEFAULT_PROTOCOL_SORT_COLUMN;
  }
  if (PROTOCOL_LIST_FIELD_TO_API_COLUMN[field]) {
    return PROTOCOL_LIST_FIELD_TO_API_COLUMN[field];
  }
  if (PROTOCOL_LIST_API_COLUMNS.has(field)) {
    return field;
  }
  return field;
};

export const buildProtocolListRequest = ({
  sortModel,
  filterModel,
  pageSize,
  pageNumber,
}: {
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
  pageSize: number;
  pageNumber: number;
}): ProtocolIntelligenceListRequest => {
  const activeSort = sortModel[0];
  const sortingColumn = resolveProtocolSortingColumn(activeSort?.field);
  const direction = activeSort?.sort === 'asc' ? 'asc' : 'desc';

  const columnFilter = (filterModel.items ?? [])
    .filter((item) => item.field && String(item.value ?? '').trim())
    .map((item) => ({
      columnName: PROTOCOL_LIST_FIELD_TO_API_COLUMN[item.field] ?? item.field,
      value: String(item.value).trim(),
    }));

  return {
    sortingColumn,
    direction,
    displayLength: pageSize,
    pageNumber,
    columnFilter,
  };
};

export const protocolColumnsData: IProtocolColumnsData[] = [
  {
    field: 'protocolName',
    headerName: t('ai.review.protocolList.columns.protocolName'),
    fieldDataKey: 'protocolName',
    filterable: true,
    sortable: true,
    width: 260,
  },
  {
    field: 'latestVersion',
    headerName: t('ai.review.protocolList.columns.latestVersion'),
    fieldDataKey: 'latestVersion',
    filterable: false,
    sortable: true,
    width: 140,
  },
  {
    field: 'source',
    headerName: t('ai.review.protocolList.columns.source'),
    fieldDataKey: 'source',
    filterable: false,
    sortable: true,
    width: 140,
  },
  {
    field: 'lastUpdatedAt',
    headerName: t('ai.review.protocolList.columns.lastUpdated'),
    fieldDataKey: 'lastUpdatedAt',
    filterable: false,
    sortable: true,
    width: 180,
    formatFunction: (value) =>
      formatProtocolDate(value as string | null | undefined),
  },
  {
    field: 'createdBy',
    headerName: t('ai.review.protocolList.columns.createdBy'),
    fieldDataKey: 'createdBy',
    filterable: true,
    sortable: true,
    width: 180,
  },
  {
    field: 'status',
    headerName: t('ai.review.protocolList.columns.status'),
    fieldDataKey: 'status',
    filterable: true,
    sortable: true,
    width: 120,
  },
];
